import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Loader2, Users, GripVertical, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    bio: '',
    image: '',
    order: 0
  });

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team/`);
      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const openCreateModal = () => {
    setEditingMember(null);
    setFormData({
      name: '',
      role: '',
      bio: '',
      image: '',
      order: teamMembers.length + 1
    });
    setShowModal(true);
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      bio: member.bio || '',
      image: member.image || '',
      order: member.order || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingMember 
        ? `${API_URL}/api/team/${editingMember.id}`
        : `${API_URL}/api/team/`;
      
      const response = await fetch(url, {
        method: editingMember ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingMember ? 'Team member updated!' : 'Team member added!');
        setShowModal(false);
        fetchTeamMembers();
      } else {
        toast.error('Failed to save team member');
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      toast.error('Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to delete ${memberName}?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/team/${memberId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Team member deleted');
        fetchTeamMembers();
      } else {
        toast.error('Failed to delete team member');
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast.error('Failed to delete team member');
    }
  };

  const seedTeam = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team/seed`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchTeamMembers();
      }
    } catch (error) {
      toast.error('Failed to seed team members');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-green" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-charcoal font-heading">Team Members</h1>
            <p className="text-stone-grey font-body mt-1">Manage your "Meet the Team" section</p>
          </div>
          <div className="flex gap-3">
            {teamMembers.length === 0 && (
              <Button 
                onClick={seedTeam}
                variant="outline" 
                className="border-accent text-accent hover:bg-accent hover:text-white px-6 py-3 h-12 text-base"
              >
                <Users className="h-5 w-5 mr-2" />
                Load Default Team
              </Button>
            )}
            <Button 
              onClick={openCreateModal}
              className="bg-muted-green hover:bg-muted-green/90 text-white px-6 py-3 h-12 text-base shadow-md hover:shadow-lg transition-all"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Team Member
            </Button>
          </div>
        </div>

        {/* Team Members Grid */}
        {teamMembers.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border border-soft-sand shadow-soft text-center">
            <Users className="h-16 w-16 text-stone-grey mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-charcoal mb-2 font-heading">No Team Members Yet</h3>
            <p className="text-stone-grey font-body mb-6">Add team members to display on your About page.</p>
            <Button 
              onClick={seedTeam}
              className="bg-muted-green hover:bg-muted-green/90 text-white px-6 py-3"
            >
              Load Default Team
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamMembers.map((member) => (
              <div 
                key={member.id} 
                className="bg-white rounded-xl border border-soft-sand shadow-soft overflow-hidden group hover:shadow-lg transition-shadow"
              >
                {/* Image */}
                <div className="aspect-square relative bg-soft-sand">
                  {member.image ? (
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users className="h-20 w-20 text-stone-grey/40" />
                    </div>
                  )}
                  {/* Order badge */}
                  <div className="absolute top-3 left-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow">
                    <span className="text-sm font-bold text-charcoal">{member.order}</span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-charcoal font-heading">{member.name}</h3>
                  <p className="text-accent font-medium font-body mb-2">{member.role}</p>
                  {member.bio && (
                    <p className="text-stone-grey text-sm font-body line-clamp-2">{member.bio}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="px-5 pb-5 flex gap-2">
                  <Button
                    onClick={() => openEditModal(member)}
                    variant="outline"
                    className="flex-1 border-muted-green text-muted-green hover:bg-muted-green hover:text-white h-10"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(member.id, member.name)}
                    variant="outline"
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white h-10 px-4"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-soft-sand">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-charcoal font-heading">
                  {editingMember ? 'Edit Team Member' : 'Add Team Member'}
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-soft-sand rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-stone-grey" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <Label className="text-charcoal font-semibold">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="text-charcoal font-semibold">Role *</Label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="CEO & Founder"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="text-charcoal font-semibold">Bio</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Short biography..."
                  rows={3}
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="text-charcoal font-semibold">Photo URL</Label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://example.com/photo.jpg"
                  className="mt-2"
                />
                {formData.image && (
                  <div className="mt-3 w-24 h-24 rounded-lg overflow-hidden border border-soft-sand">
                    <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div>
                <Label className="text-charcoal font-semibold">Display Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="mt-2 w-24"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1 h-12 text-base"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-muted-green hover:bg-muted-green/90 text-white h-12 text-base shadow-md"
                >
                  {saving ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Save className="h-5 w-5 mr-2" />
                  )}
                  {editingMember ? 'Save Changes' : 'Add Member'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTeam;
